import { z } from "zod";
import { SOCKET_NOT_TAKEN, type SocketType } from "../types";

/**
 * Get the Zod type name from a schema using Zod v4's traits system
 */
export function getZodTypeName(schema: z.ZodType): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const traits = (schema as any)._zod?.traits as Set<string> | undefined;
  if (!traits) return undefined;

  // Find the first trait that starts with "Zod" but not "ZodType"
  for (const trait of traits) {
    if (trait.startsWith("Zod") && trait !== "ZodType") {
      return trait;
    }
  }
  return undefined;
}

/**
 * Get the inner/wrapped type from optional, nullable, default, or pipe schemas
 */
export function getInnerType(schema: z.ZodType): z.ZodType | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const def = (schema as any)._zod?.def;
  // Zod v4 uses innerType for optional/nullable/default
  if (def?.innerType) return def.innerType;
  // ZodPipe (preprocess, .transform, etc.): `out` is the validated/output shape.
  // Following `in` hits ZodTransform and breaks entity-ref / UUID inference.
  if (def?.out) return def.out;
  if (def?.in) return def.in;
  return undefined;
}

/**
 * Unwrap optional/nullable/default/effects wrappers to the innermost schema.
 */
export function unwrapSchema(schema: z.ZodType): z.ZodType {
  let current = schema;
  let typeName = getZodTypeName(current);

  while (
    typeName === "ZodOptional" ||
    typeName === "ZodNullable" ||
    typeName === "ZodDefault" ||
    typeName === "ZodEffects" ||
    typeName === "ZodPipe" ||
    // Zod 4.3+ gives `z.preprocess(...)` its own trait that sorts ahead of
    // ZodPipe, so getZodTypeName() reports "ZodPreprocess". Unwrap it too.
    typeName === "ZodPreprocess"
  ) {
    const inner = getInnerType(current);
    if (!inner) break;
    current = inner;
    typeName = getZodTypeName(current);
  }

  return current;
}

/**
 * Infer the socket type from a Zod field schema
 *
 * This utility examines a Zod schema and determines the appropriate
 * socket type for workflow step inputs/outputs. It handles:
 * - Primitive types (string, number, boolean, date)
 * - UUIDs (detected via Zod checks)
 * - Entity references (objects with entityType + entityId)
 * - Arrays (including typed entity reference arrays for fanout)
 * - Unions, enums, literals
 * - Optional/nullable wrappers
 */
export function inferSocketTypeFromZod(fieldSchema: z.ZodType): SocketType {
  const typeName = getZodTypeName(fieldSchema);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const def = (fieldSchema as any)._zod?.def;

  // Handle optional/nullable wrappers - unwrap and recurse
  if (typeName === "ZodOptional" || typeName === "ZodNullable") {
    const inner = getInnerType(fieldSchema);
    if (inner) return inferSocketTypeFromZod(inner);
  }

  // Handle default wrappers
  if (typeName === "ZodDefault") {
    const inner = getInnerType(fieldSchema);
    if (inner) return inferSocketTypeFromZod(inner);
  }

  // Handle effects/pipe (like .transform()) - Zod v4 uses ZodPipe.
  // Zod 4.3+ also tags `z.preprocess(...)` with a "ZodPreprocess" trait that
  // getZodTypeName() reports ahead of "ZodPipe"; both unwrap via getInnerType.
  if (
    typeName === "ZodEffects" ||
    typeName === "ZodPipe" ||
    typeName === "ZodPreprocess"
  ) {
    const inner = getInnerType(fieldSchema);
    if (inner) return inferSocketTypeFromZod(inner);
  }

  // Primitive types
  if (typeName === "ZodString") {
    // Check for UUID format - Zod v4 stores format directly on schema or in checks
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const format = (fieldSchema as any).format;
    if (format === "uuid") return "uuid";

    // Also check checks array for UUID
    const checks = def?.checks || [];
    const isUuid = checks.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (check: any) => check.format === "uuid" || check.kind === "uuid",
    );
    if (isUuid) return "uuid";
    return "string";
  }

  if (typeName === "ZodNumber") {
    return "number";
  }

  if (typeName === "ZodBoolean") {
    return "boolean";
  }

  if (typeName === "ZodDate") {
    return "date";
  }

  // z.uuid() — Zod v4 uses ZodUUID instead of ZodString + format
  if (typeName === "ZodUUID" || typeName === "ZodGUID") {
    return "uuid";
  }

  // Array types - Zod v4 uses 'element' instead of 'type'
  if (typeName === "ZodArray") {
    const elementSchema = def?.element;
    if (elementSchema) {
      const elementType = inferSocketTypeFromZod(elementSchema);
      if (elementType === "string") return "array<string>";
      if (elementType === "number") return "array<number>";
      if (elementType === "boolean") return "array<boolean>";
      if (elementType === "uuid") return "array<uuid>";
      // Handle typed entity references like "entity_reference:CORE_DOCUMENT"
      if (elementType.startsWith("entity_reference")) {
        return `array<${elementType}>` as SocketType;
      }
      // All other element types (json, objects, etc.) - use array<json>
      return "array<json>" as SocketType;
    }
    // Default to json array when no element schema available
    return "array<json>" as SocketType;
  }

  // Object types - typically entity_reference or json
  if (typeName === "ZodObject") {
    // Check if it looks like an entity reference (has entityType and entityId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shape = (fieldSchema as any).shape;
    if (shape && shape.entityType && shape.entityId) {
      // Try to extract the specific entity type from the literal
      const entityTypeSchema = shape.entityType;
      const entityTypeName = getZodTypeName(entityTypeSchema);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entityTypeDef = (entityTypeSchema as any)?._zod?.def;

      if (entityTypeName === "ZodLiteral" && entityTypeDef?.values?.[0]) {
        // Zod v4 stores literal values in 'values' array
        return `entity_reference:${entityTypeDef.values[0]}` as SocketType;
      }
      // Generic entity reference
      return "entity_reference";
    }
    // Otherwise treat as JSON
    return "json";
  }

  // Enum types - treat as string (Zod v4 uses ZodEnum for both enum and nativeEnum)
  if (typeName === "ZodEnum") {
    return "string";
  }

  // Union types - check if it's a simple string union
  if (typeName === "ZodUnion") {
    const options = def?.options || [];

    // Transparency for `conditionalSocket(X)`: it wraps a schema in a union
    // with the SOCKET_NOT_TAKEN literal so handlers can emit "branch not
    // taken" at runtime. The sentinel is a runtime-only marker, not a real
    // type — the socket should still be typed as X for edge compatibility
    // checks. Unwrap and recurse on the non-sentinel option.
    if (options.length === 2) {
      // Narrow internal view of a ZodLiteral's underlying def. Zod's public
      // types don't expose `_zod`, but we only need to read the literal's
      // first value to detect the SOCKET_NOT_TAKEN sentinel.
      interface ZodLiteralLike {
        _zod?: { def?: { values?: unknown[] } };
      }
      const isNotTakenLiteral = (opt: z.ZodType): boolean => {
        const optDef = (opt as ZodLiteralLike)._zod?.def;
        return (
          getZodTypeName(opt) === "ZodLiteral" &&
          optDef?.values?.[0] === SOCKET_NOT_TAKEN
        );
      };
      const notTakenIdx = options.findIndex(isNotTakenLiteral);
      if (notTakenIdx !== -1) {
        return inferSocketTypeFromZod(options[1 - notTakenIdx]);
      }
    }

    const allStrings = options.every(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (opt: any) => {
        const optTypeName = getZodTypeName(opt);
        return optTypeName === "ZodLiteral" || optTypeName === "ZodString";
      },
    );
    if (allStrings) return "string";
    return "json";
  }

  // Literal types - Zod v4 stores value in 'values' array
  if (typeName === "ZodLiteral") {
    const value = def?.values?.[0];
    if (typeof value === "string") return "string";
    if (typeof value === "number") return "number";
    if (typeof value === "boolean") return "boolean";
    return "json";
  }

  // Record types - treat as json
  if (typeName === "ZodRecord") {
    return "json";
  }

  // Void/undefined - used for signals
  if (typeName === "ZodVoid" || typeName === "ZodUndefined") {
    return "signal";
  }

  // Default fallback
  return "any";
}

/**
 * Check if a Zod field schema represents an optional field
 *
 * Returns true if the schema is wrapped in ZodOptional, ZodNullable,
 * or ZodDefault (since having a default makes it effectively optional).
 */
export function isFieldOptional(fieldSchema: z.ZodType): boolean {
  const typeName = getZodTypeName(fieldSchema);
  if (typeName === "ZodOptional" || typeName === "ZodNullable") {
    return true;
  }
  if (typeName === "ZodDefault") {
    return true; // Has a default, so effectively optional
  }
  return false;
}

/**
 * Socket definition type for generated sockets
 */
export interface GeneratedSocket {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

/**
 * Generate socket definitions from a Zod schema
 *
 * This converts a Zod object schema into an array of socket definitions,
 * inferring the socket type for each field. Used for both input and output
 * sockets in workflow step definitions.
 *
 * @param schema - The Zod schema to convert
 * @param socketKind - Whether these are "input" or "output" sockets (for descriptions)
 * @returns Array of socket definitions
 */
export function generateSocketsFromSchema(
  schema: z.ZodType,
  socketKind: "input" | "output",
): GeneratedSocket[] {
  const unwrapped = unwrapSchema(schema);
  const typeName = getZodTypeName(unwrapped);

  // For z.never() (event triggers with no inputs), return empty array
  if (typeName === "ZodNever") {
    return [];
  }

  // For z.object(), extract keys and infer types
  if (typeName === "ZodObject") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shape = (unwrapped as any).shape;
    if (shape && typeof shape === "object") {
      return Object.entries(shape).map(([key, fieldSchema]) => {
        const zodField = fieldSchema as z.ZodType;
        const inferredType = inferSocketTypeFromZod(zodField);
        const required = !isFieldOptional(zodField);
        // A `.meta({ title })` on the field overrides the prettified key as
        // the socket's display name — the id stays the key, so persisted
        // edges keep resolving when a socket is given a friendlier label.
        const metaTitle =
          zodField.meta?.()?.title ?? unwrapSchema(zodField).meta?.()?.title;

        return {
          id: key,
          name:
            typeof metaTitle === "string" && metaTitle.length > 0
              ? metaTitle
              : key
                  // Insert space before uppercase letters that follow lowercase letters
                  .replace(/([a-z])([A-Z])/g, "$1 $2")
                  // Insert space before uppercase letters followed by lowercase (for acronyms)
                  .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
                  // Capitalize first letter
                  .replace(/^./, (str) => str.toUpperCase()),
          type: inferredType,
          required,
          description: `${socketKind === "input" ? "Input" : "Output"} field: ${key}`,
        };
      });
    }
  }

  // z.record() / z.map() mean "dynamic keys filled in by config or the
  // handler" — not a single concrete socket. Inventing a required "Value"
  // socket here made steps like famous_sql_query / code_sandbox fail
  // Save & Publish with "missing required input Value" when no keys were
  // declared yet. Static sockets for those steps come from their dynamic
  // generators instead.
  if (typeName === "ZodRecord" || typeName === "ZodMap") {
    return [];
  }

  // For single-value schemas, return a single socket with inferred type
  const singleType = inferSocketTypeFromZod(unwrapped);
  return [
    {
      id: "value",
      name: "Value",
      type: singleType,
      required: true,
      description: `${socketKind === "input" ? "Input" : "Output"} value`,
    },
  ];
}
